import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import type { ReOSDatabase } from "../domain/types";

/** One workspace poller survives navigation and resumes from persisted jobs after reload. */
export function usePhotoImportRefresh(database: ReOSDatabase, setDatabase: Dispatch<SetStateAction<ReOSDatabase>>,
  load: (workspaceID: string) => Promise<ReOSDatabase>, enabled: boolean) {
  const latest = useRef({ database, load }); latest.current = { database, load };
  const workspaceID = database.activeWorkspaceID;
  const userID = database.currentUserID;
  const pending = enabled && (database.channelPhotoJobs ?? []).some((job) => job.workspaceID === workspaceID && ["queued", "saving"].includes(job.status));
  useEffect(() => {
    if (!pending || !workspaceID || !userID) return;
    let active = true; let busy = false; let failures = 0; let timer: number | undefined;
    const schedule = () => { if (active) timer = window.setTimeout(refresh, Math.min(30_000, 2000 * 2 ** failures)); };
    async function refresh() {
      window.clearTimeout(timer);
      if (!active || busy) return;
      if (document.visibilityState === "hidden" || !navigator.onLine) { schedule(); return; }
      busy = true;
      const before = latest.current.database;
      try {
        const loaded = await latest.current.load(workspaceID!);
        if (!active) return;
        failures = 0;
        // A response started before an edit, sign-out or workspace switch cannot overwrite it.
        if (loaded.activeWorkspaceID === workspaceID && loaded.currentUserID === userID) {
          setDatabase((current) => current === before ? loaded : current);
        }
      } catch { failures = Math.min(4, failures + 1); }
      finally { busy = false; schedule(); }
    }
    const wake = () => { if (document.visibilityState !== "hidden") void refresh(); };
    schedule();
    window.addEventListener("focus", wake); window.addEventListener("online", wake); document.addEventListener("visibilitychange", wake);
    return () => { active = false; window.clearTimeout(timer); window.removeEventListener("focus", wake); window.removeEventListener("online", wake); document.removeEventListener("visibilitychange", wake); };
  }, [pending, workspaceID, userID, setDatabase]);
}
